import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ExternalLink,
  MailCheck,
  Pause,
  Pencil,
  Play,
  Plus,
  Rocket,
  Upload,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { api, jsonBody } from './api'
import { useAuth } from './auth'
import { PageHeader, StatusPill, TableShell } from './ui'

interface PlatformTenant {
  id: string
  name: string
  slug: string
  timezone: string
  subscriptionStatus: string | null
  planId: string | null
  plan: string | null
  stripeManaged: boolean
  devices: number
  users: number
  primaryColor?: string | null
  mfaEnforced?: boolean
  createdAt: string
}

interface PlatformTenantAdmin {
  membershipId: string
  userId: string
  name: string
  email: string
  role: 'TENANT_OWNER' | 'TENANT_ADMIN'
  emailVerified: boolean
  mfaEnabled: boolean
  disabled: boolean
  joinedAt: string
}

interface PlatformAdminInvitation {
  id: string
  email: string
  role: 'TENANT_OWNER' | 'TENANT_ADMIN'
  expiresAt: string
  createdAt: string
  invitationUrl?: string
}

interface PlatformTenantDetail extends PlatformTenant {
  primaryColor: string | null
  mfaEnforced: boolean
  admins: PlatformTenantAdmin[]
  pendingAdminInvitations: PlatformAdminInvitation[]
}

interface PlatformTenantCreation {
  tenant: PlatformTenant
  ownerInvitation: {
    id: string
    email: string
    role: 'TENANT_OWNER'
    expiresAt: string
    invitationUrl: string
  }
}

interface TenantPlanAssignment {
  tenantId: string
  planId: string
  plan: string
  subscriptionStatus: string
  stripeManaged: boolean
}

interface Plan {
  id: string
  code: string
  name: string
  stripePriceId: string | null
  deviceLimit: number
  userLimit: number
  storageBytes: number
  active: boolean
  sortOrder: number
}

interface PlanInput {
  code?: string
  name: string
  stripePriceId: string | null
  deviceLimit: number
  userLimit: number
  storageBytes: number
  active: boolean
  sortOrder: number
}

interface Release {
  id: string
  versionCode: number
  versionName: string
  packageName: string
  checksumSha256: string
  certificateSha256: string
  sizeBytes: number
  minimumVersionCode: number
  rolloutPercent: number
  paused: boolean
  status: string
  validationError: string | null
  validatedAt: string | null
  publishedAt: string | null
  createdAt: string
}

interface ReleaseUpload extends Release {
  uploadUrl: string
  uploadExpiresAt: string
}

interface EmailGateway {
  provider: 'SMTP' | 'ZEPTOMAIL'
  enabled: boolean
  host: string
  port: number
  secure: boolean
  fromName: string
  fromEmail: string
  username: string
  passwordConfigured: boolean
  lastTestedAt: string | null
  lastTestStatus: string | null
  lastTestError: string | null
  updatedAt: string | null
}

function EmailGatewayForm({
  initial,
  defaultRecipient,
}: {
  initial: EmailGateway
  defaultRecipient: string
}) {
  const queryClient = useQueryClient()
  const [provider, setProvider] = useState(initial.provider)
  const [enabled, setEnabled] = useState(initial.enabled)
  const [host, setHost] = useState(initial.host)
  const [port, setPort] = useState(String(initial.port))
  const [secure, setSecure] = useState(initial.secure)
  const [fromName, setFromName] = useState(initial.fromName)
  const [fromEmail, setFromEmail] = useState(initial.fromEmail)
  const [username, setUsername] = useState(initial.username)
  const [password, setPassword] = useState('')
  const [clearPassword, setClearPassword] = useState(false)
  const [testRecipient, setTestRecipient] = useState(defaultRecipient)

  const save = useMutation({
    mutationFn: () =>
      api<EmailGateway>('/platform/email-gateway', {
        method: 'PUT',
        body: jsonBody({
          provider,
          enabled,
          host,
          port: Number(port),
          secure,
          fromName,
          fromEmail,
          username,
          password: password || undefined,
          clearPassword,
        }),
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['platform-email-gateway'], saved)
      toast.success('Email gateway settings saved.')
    },
    onError: (error) => toast.error(error.message),
  })
  const test = useMutation({
    mutationFn: () =>
      api('/platform/email-gateway/test', {
        method: 'POST',
        body: jsonBody({ recipient: testRecipient }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['platform-email-gateway'],
      })
      toast.success('Test email queued. Delivery status will update here.')
    },
    onError: (error) => toast.error(error.message),
  })

  const selectProvider = (value: 'SMTP' | 'ZEPTOMAIL') => {
    setProvider(value)
    if (value === 'ZEPTOMAIL') {
      setHost('smtp.zeptomail.com')
      setPort('587')
      setSecure(false)
      if (!username) setUsername('emailapikey')
    }
  }

  return (
    <div className='grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,.6fr)]'>
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>Gateway configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className='grid gap-4 md:grid-cols-2'
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate()
            }}
          >
            <div className='space-y-2'>
              <Label htmlFor='email-provider'>Provider</Label>
              <select
                id='email-provider'
                className='h-10 w-full rounded-md border bg-background px-3 text-sm'
                value={provider}
                onChange={(event) =>
                  selectProvider(event.target.value as 'SMTP' | 'ZEPTOMAIL')
                }
              >
                <option value='ZEPTOMAIL'>ZeptoMail</option>
                <option value='SMTP'>Custom SMTP</option>
              </select>
            </div>
            <label className='flex min-h-10 items-center gap-3 self-end rounded-md border px-3 text-sm'>
              <input
                type='checkbox'
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
              />
              Enable transactional email delivery
            </label>
            <div className='space-y-2'>
              <Label htmlFor='email-host'>SMTP host</Label>
              <Input
                id='email-host'
                value={host}
                onChange={(event) => setHost(event.target.value)}
                readOnly={provider === 'ZEPTOMAIL'}
                required
              />
            </div>
            <div className='grid grid-cols-[1fr_auto] gap-3'>
              <div className='space-y-2'>
                <Label htmlFor='email-port'>Port</Label>
                <Input
                  id='email-port'
                  type='number'
                  min={1}
                  max={65535}
                  value={port}
                  onChange={(event) => setPort(event.target.value)}
                  required
                />
              </div>
              <label className='flex items-end gap-2 pb-2 text-sm'>
                <input
                  type='checkbox'
                  checked={secure}
                  onChange={(event) => setSecure(event.target.checked)}
                />
                SSL/TLS
              </label>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='email-from-name'>From name</Label>
              <Input
                id='email-from-name'
                value={fromName}
                onChange={(event) => setFromName(event.target.value)}
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='email-from-address'>From email</Label>
              <Input
                id='email-from-address'
                type='email'
                value={fromEmail}
                onChange={(event) => setFromEmail(event.target.value)}
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='email-username'>SMTP username</Label>
              <Input
                id='email-username'
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder={provider === 'ZEPTOMAIL' ? 'emailapikey' : ''}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='email-password'>
                {initial.passwordConfigured
                  ? 'Replace SMTP password'
                  : 'SMTP password'}
              </Label>
              <Input
                id='email-password'
                type='password'
                autoComplete='new-password'
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value)
                  if (event.target.value) setClearPassword(false)
                }}
                placeholder={
                  initial.passwordConfigured
                    ? 'Leave blank to keep the stored password'
                    : 'Enter provider password'
                }
              />
            </div>
            {initial.passwordConfigured && (
              <label className='flex items-center gap-2 text-sm md:col-span-2'>
                <input
                  type='checkbox'
                  checked={clearPassword}
                  onChange={(event) => {
                    setClearPassword(event.target.checked)
                    if (event.target.checked) setPassword('')
                  }}
                />
                Remove the currently stored password
              </label>
            )}
            <div className='rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground md:col-span-2'>
              ZeptoMail preset uses smtp.zeptomail.com with STARTTLS on port
              587. Select SSL/TLS with port 465 if you prefer implicit TLS. The
              password is encrypted at rest and is never returned to the
              browser.
            </div>
            <Button className='md:col-span-2' disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save email gateway'}
            </Button>
          </form>
        </CardContent>
      </Card>
      <Card className='h-fit'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-base'>
            <MailCheck className='size-4' />
            Send a test email
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='email-test-recipient'>Recipient</Label>
            <Input
              id='email-test-recipient'
              type='email'
              value={testRecipient}
              onChange={(event) => setTestRecipient(event.target.value)}
              required
            />
          </div>
          <Button
            type='button'
            variant='outline'
            className='w-full'
            disabled={test.isPending || !initial.enabled}
            onClick={() => test.mutate()}
          >
            {test.isPending ? 'Queueing…' : 'Send test email'}
          </Button>
          <div className='rounded-lg border p-3 text-sm'>
            <div className='flex items-center justify-between gap-3'>
              <span className='text-muted-foreground'>Gateway</span>
              <StatusPill value={initial.enabled ? 'ENABLED' : 'DISABLED'} />
            </div>
            <div className='mt-3 flex items-center justify-between gap-3'>
              <span className='text-muted-foreground'>Last test</span>
              <StatusPill value={initial.lastTestStatus ?? 'NOT_TESTED'} />
            </div>
            {initial.lastTestedAt && (
              <p className='mt-2 text-xs text-muted-foreground'>
                {new Date(initial.lastTestedAt).toLocaleString()}
              </p>
            )}
            {initial.lastTestError && (
              <p className='mt-2 text-xs break-words text-red-600'>
                {initial.lastTestError}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function PlatformEmailGatewayPage() {
  const { session } = useAuth()
  const gateway = useQuery({
    queryKey: ['platform-email-gateway'],
    queryFn: () => api<EmailGateway>('/platform/email-gateway'),
    refetchInterval: (query) =>
      query.state.data?.lastTestStatus === 'QUEUED' ? 2_000 : false,
  })
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Platform'
        title='Email gateway'
        description='Configure the global transactional email provider used for verification, password reset, tenant invitations, and platform notices.'
      />
      {gateway.data && (
        <EmailGatewayForm
          key={gateway.data.updatedAt ?? 'new-email-gateway'}
          initial={gateway.data}
          defaultRecipient={session?.user.email ?? ''}
        />
      )}
    </div>
  )
}

function TenantAdminEditor({
  tenantId,
  admin,
}: {
  tenantId: string
  admin: PlatformTenantAdmin
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(admin.name)
  const [email, setEmail] = useState(admin.email)
  const [role, setRole] = useState(admin.role)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const passwordMismatch =
    newPassword.length > 0 && newPassword !== confirmPassword
  const passwordTooShort = newPassword.length > 0 && newPassword.length < 12
  const update = useMutation({
    mutationFn: () =>
      api<PlatformTenantAdmin>(
        `/platform/tenants/${tenantId}/admins/${admin.membershipId}`,
        {
          method: 'PATCH',
          body: jsonBody({
            name,
            email,
            role,
            ...(newPassword ? { newPassword } : {}),
          }),
        }
      ),
    onSuccess: (saved) => {
      const emailChanged =
        saved.email.trim().toLowerCase() !== admin.email.trim().toLowerCase()
      const passwordChanged = newPassword.length > 0
      const emailAutoVerified = !admin.emailVerified && saved.emailVerified
      setNewPassword('')
      setConfirmPassword('')
      queryClient.setQueryData<PlatformTenantDetail>(
        ['platform-tenant', tenantId],
        (current) =>
          current
            ? {
                ...current,
                admins: current.admins.map((item) =>
                  item.membershipId === saved.membershipId ? saved : item
                ),
              }
            : current
      )
      toast.success(
        emailChanged && passwordChanged
          ? 'Login email and password updated. The email is verified and all sessions were signed out.'
          : emailChanged
            ? 'Login email updated and verified automatically. All active sessions were signed out.'
            : passwordChanged && emailAutoVerified
              ? 'Password updated, email verified, and all active sessions signed out.'
              : passwordChanged
                ? 'Password updated. All active sessions were signed out.'
                : emailAutoVerified
                  ? 'The login email was verified automatically.'
                  : 'Tenant administrator updated.'
      )
    },
    onError: (error) => toast.error(error.message),
  })
  return (
    <div className='grid gap-4 rounded-xl border bg-muted/20 p-4 md:grid-cols-2 xl:grid-cols-[minmax(180px,1fr)_minmax(240px,1.35fr)_200px_auto] xl:items-end'>
      <div className='space-y-2'>
        <Label htmlFor={`admin-name-${admin.membershipId}`}>Name</Label>
        <Input
          id={`admin-name-${admin.membershipId}`}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className='space-y-2'>
        <Label htmlFor={`admin-email-${admin.membershipId}`}>Login email</Label>
        <Input
          id={`admin-email-${admin.membershipId}`}
          type='email'
          value={email}
          required
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>
      <div className='space-y-2'>
        <Label htmlFor={`admin-role-${admin.membershipId}`}>Role</Label>
        <select
          id={`admin-role-${admin.membershipId}`}
          className='h-10 w-full rounded-md border bg-background px-3 text-sm'
          value={role}
          onChange={(event) =>
            setRole(event.target.value as 'TENANT_OWNER' | 'TENANT_ADMIN')
          }
        >
          <option value='TENANT_OWNER'>Tenant owner</option>
          <option value='TENANT_ADMIN'>Tenant administrator</option>
        </select>
      </div>
      <div className='space-y-2 md:col-span-1 xl:col-span-2'>
        <Label htmlFor={`admin-password-${admin.membershipId}`}>
          New password (optional)
        </Label>
        <Input
          id={`admin-password-${admin.membershipId}`}
          type='password'
          autoComplete='new-password'
          value={newPassword}
          placeholder='Minimum 12 characters'
          onChange={(event) => setNewPassword(event.target.value)}
        />
      </div>
      <div className='space-y-2 md:col-span-1 xl:col-span-2'>
        <Label htmlFor={`admin-password-confirm-${admin.membershipId}`}>
          Confirm new password
        </Label>
        <Input
          id={`admin-password-confirm-${admin.membershipId}`}
          type='password'
          autoComplete='new-password'
          value={confirmPassword}
          disabled={!newPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
        />
      </div>
      {(passwordTooShort || passwordMismatch) && (
        <p className='text-xs text-red-600 md:col-span-2 xl:col-span-4'>
          {passwordTooShort
            ? 'The new password must contain at least 12 characters.'
            : 'The password confirmation does not match.'}
        </p>
      )}
      <Button
        type='button'
        variant='outline'
        className='w-full md:col-span-2 xl:col-span-1 xl:w-auto'
        disabled={
          update.isPending ||
          name.trim().length < 2 ||
          !email.trim().includes('@') ||
          passwordTooShort ||
          passwordMismatch
        }
        onClick={() => update.mutate()}
      >
        {update.isPending ? 'Saving…' : 'Update admin'}
      </Button>
      <div className='flex flex-wrap gap-2 text-xs text-muted-foreground md:col-span-2 xl:col-span-4'>
        <span>
          {admin.emailVerified ? 'Email verified' : 'Email unverified'}
        </span>
        <span>·</span>
        <span>{admin.mfaEnabled ? 'MFA enabled' : 'MFA not enabled'}</span>
        {admin.disabled && (
          <>
            <span>·</span>
            <span className='text-red-600'>Account disabled</span>
          </>
        )}
      </div>
      <p className='text-xs text-muted-foreground md:col-span-2 xl:col-span-4'>
        Email and password changes update this user account everywhere and sign
        out its active sessions. Emails managed by a platform administrator are
        verified automatically.
      </p>
    </div>
  )
}

function TenantManagementContent({ detail }: { detail: PlatformTenantDetail }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(detail.name)
  const [timezone, setTimezone] = useState(detail.timezone)
  const [mfaEnforced, setMfaEnforced] = useState(detail.mfaEnforced)
  const [primaryColor, setPrimaryColor] = useState(
    detail.primaryColor ?? '#ef4444'
  )
  const [usePrimaryColor, setUsePrimaryColor] = useState(
    Boolean(detail.primaryColor)
  )
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'TENANT_OWNER' | 'TENANT_ADMIN'>(
    'TENANT_ADMIN'
  )
  const [createdInvitation, setCreatedInvitation] =
    useState<PlatformAdminInvitation>()

  const updateTenant = useMutation({
    mutationFn: () =>
      api<PlatformTenant>(`/platform/tenants/${detail.id}`, {
        method: 'PATCH',
        body: jsonBody({
          name,
          timezone,
          primaryColor: usePrimaryColor ? primaryColor : null,
          mfaEnforced,
        }),
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData<PlatformTenantDetail>(
        ['platform-tenant', detail.id],
        (current) => (current ? { ...current, ...saved } : current)
      )
      queryClient.setQueryData<PlatformTenant[]>(
        ['platform-tenants'],
        (current) =>
          current?.map((tenant) =>
            tenant.id === detail.id ? { ...tenant, ...saved } : tenant
          )
      )
      toast.success('Tenant information updated.')
    },
    onError: (error) => toast.error(error.message),
  })
  const invite = useMutation({
    mutationFn: () =>
      api<PlatformAdminInvitation>(
        `/platform/tenants/${detail.id}/admin-invitations`,
        {
          method: 'POST',
          body: jsonBody({ email: inviteEmail, role: inviteRole }),
        }
      ),
    onSuccess: (created) => {
      setCreatedInvitation(created)
      setInviteEmail('')
      queryClient.setQueryData<PlatformTenantDetail>(
        ['platform-tenant', detail.id],
        (current) =>
          current
            ? {
                ...current,
                pendingAdminInvitations: [
                  created,
                  ...current.pendingAdminInvitations.filter(
                    (item) => item.email !== created.email
                  ),
                ],
              }
            : current
      )
      toast.success('Administrator invitation created and email queued.')
    },
    onError: (error) => toast.error(error.message),
  })

  return (
    <div className='space-y-7'>
      <section className='space-y-4'>
        <div>
          <h3 className='font-semibold'>Tenant information</h3>
          <p className='text-sm text-muted-foreground'>
            Update organization identity, timezone, and security policy.
          </p>
        </div>
        <form
          className='grid gap-4 md:grid-cols-2'
          onSubmit={(event) => {
            event.preventDefault()
            updateTenant.mutate()
          }}
        >
          <div className='space-y-2'>
            <Label htmlFor='platform-edit-tenant-name'>Organization name</Label>
            <Input
              id='platform-edit-tenant-name'
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='platform-edit-tenant-timezone'>IANA timezone</Label>
            <Input
              id='platform-edit-tenant-timezone'
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
              required
            />
          </div>
          <label className='flex items-center gap-3 rounded-md border px-3 py-2 text-sm'>
            <input
              type='checkbox'
              checked={usePrimaryColor}
              onChange={(event) => setUsePrimaryColor(event.target.checked)}
            />
            Use custom brand color
            <input
              aria-label='Tenant brand color'
              type='color'
              value={primaryColor}
              disabled={!usePrimaryColor}
              onChange={(event) => setPrimaryColor(event.target.value)}
            />
          </label>
          <label className='flex items-center gap-3 rounded-md border px-3 py-2 text-sm'>
            <input
              type='checkbox'
              checked={mfaEnforced}
              onChange={(event) => setMfaEnforced(event.target.checked)}
            />
            Require MFA for this tenant
          </label>
          <Button className='md:col-span-2' disabled={updateTenant.isPending}>
            {updateTenant.isPending
              ? 'Saving tenant…'
              : 'Save tenant information'}
          </Button>
        </form>
      </section>

      <section className='space-y-4 border-t pt-6'>
        <div>
          <h3 className='font-semibold'>Owners and administrators</h3>
          <p className='text-sm text-muted-foreground'>
            Names, login emails, passwords, and tenant roles can be updated
            here. Account changes apply to that user everywhere.
          </p>
        </div>
        {detail.admins.length ? (
          detail.admins.map((admin) => (
            <TenantAdminEditor
              key={admin.membershipId}
              tenantId={detail.id}
              admin={admin}
            />
          ))
        ) : (
          <p className='rounded-lg border border-dashed p-4 text-sm text-muted-foreground'>
            No owner or administrator has accepted an invitation yet.
          </p>
        )}
      </section>

      <section className='space-y-4 border-t pt-6'>
        <div>
          <h3 className='font-semibold'>Invite another administrator</h3>
          <p className='text-sm text-muted-foreground'>
            A seven-day single-use link is emailed and also shown once below.
          </p>
        </div>
        <form
          className='grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_220px_auto] xl:items-end'
          onSubmit={(event) => {
            event.preventDefault()
            invite.mutate()
          }}
        >
          <div className='space-y-2'>
            <Label htmlFor='platform-new-admin-email'>
              Administrator email
            </Label>
            <Input
              id='platform-new-admin-email'
              aria-label='New administrator email'
              type='email'
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder='admin@example.com'
              required
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='platform-new-admin-role'>Tenant role</Label>
            <select
              id='platform-new-admin-role'
              aria-label='New administrator role'
              className='h-10 w-full rounded-md border bg-background px-3 text-sm'
              value={inviteRole}
              onChange={(event) =>
                setInviteRole(
                  event.target.value as 'TENANT_OWNER' | 'TENANT_ADMIN'
                )
              }
            >
              <option value='TENANT_ADMIN'>Tenant administrator</option>
              <option value='TENANT_OWNER'>Tenant owner</option>
            </select>
          </div>
          <Button
            className='w-full md:col-span-2 xl:col-span-1'
            disabled={invite.isPending}
          >
            {invite.isPending ? 'Inviting…' : 'Invite admin'}
          </Button>
        </form>
        {createdInvitation?.invitationUrl && (
          <div className='space-y-2 rounded-lg border bg-muted/30 p-3'>
            <Label htmlFor='platform-admin-invitation-link'>
              Administrator invitation link
            </Label>
            <div className='flex gap-2'>
              <Input
                id='platform-admin-invitation-link'
                value={createdInvitation.invitationUrl}
                readOnly
              />
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  void navigator.clipboard
                    .writeText(createdInvitation.invitationUrl ?? '')
                    .then(() => toast.success('Invitation link copied.'))
                    .catch(() => toast.error('Could not copy the link.'))
                }}
              >
                Copy
              </Button>
            </div>
          </div>
        )}
        {detail.pendingAdminInvitations.length > 0 && (
          <div className='space-y-2'>
            <p className='text-xs font-semibold tracking-wider text-muted-foreground uppercase'>
              Pending invitations
            </p>
            {detail.pendingAdminInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className='flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm'
              >
                <span>{invitation.email}</span>
                <span className='text-muted-foreground'>
                  {invitation.role === 'TENANT_OWNER' ? 'Owner' : 'Admin'} ·
                  expires {new Date(invitation.expiresAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function TenantManagementDialog({
  tenant,
  close,
}: {
  tenant: PlatformTenant
  close: () => void
}) {
  const detail = useQuery({
    queryKey: ['platform-tenant', tenant.id],
    queryFn: () => api<PlatformTenantDetail>(`/platform/tenants/${tenant.id}`),
  })
  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className='max-h-[92vh] w-[calc(100vw-2rem)] overflow-x-hidden overflow-y-auto sm:max-w-5xl'>
        <DialogHeader>
          <DialogTitle>Manage {tenant.name}</DialogTitle>
          <DialogDescription>
            Platform-level tenant information and administrator management. All
            changes are audited.
          </DialogDescription>
        </DialogHeader>
        {detail.data && (
          <TenantManagementContent key={detail.data.id} detail={detail.data} />
        )}
        {detail.isLoading && (
          <p className='py-8 text-center text-sm text-muted-foreground'>
            Loading tenant details…
          </p>
        )}
        {detail.isError && (
          <p className='py-8 text-center text-sm text-red-600'>
            {detail.error.message}
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}

function TenantPackageDialog({
  tenant,
  close,
}: {
  tenant: PlatformTenant
  close: () => void
}) {
  const queryClient = useQueryClient()
  const [planId, setPlanId] = useState(tenant.planId ?? '')
  const plans = useQuery({
    queryKey: ['platform-plans'],
    queryFn: () => api<Plan[]>('/platform/plans'),
  })
  const selectedPlan = plans.data?.find((plan) => plan.id === planId)
  const assignment = useMutation({
    mutationFn: () =>
      api<TenantPlanAssignment>(`/platform/tenants/${tenant.id}/plan`, {
        method: 'PATCH',
        body: jsonBody({ planId }),
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData<PlatformTenant[]>(
        ['platform-tenants'],
        (current) =>
          current?.map((item) =>
            item.id === tenant.id ? { ...item, ...saved } : item
          )
      )
      queryClient.setQueryData<PlatformTenantDetail>(
        ['platform-tenant', tenant.id],
        (current) => (current ? { ...current, ...saved } : current)
      )
      close()
      toast.success(`${tenant.name} now uses ${saved.plan}.`)
    },
    onError: (error) => toast.error(error.message),
  })
  const unchanged = !planId || planId === tenant.planId

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change {tenant.name} package</DialogTitle>
          <DialogDescription>
            Assign an active package and apply its entitlement limits to this
            tenant.
          </DialogDescription>
        </DialogHeader>
        <form
          className='space-y-5'
          onSubmit={(event) => {
            event.preventDefault()
            if (!unchanged) assignment.mutate()
          }}
        >
          <div className='space-y-2'>
            <Label htmlFor='platform-tenant-package'>Package</Label>
            <select
              id='platform-tenant-package'
              aria-label='Tenant package'
              className='h-10 w-full rounded-md border bg-background px-3 text-sm'
              value={planId}
              onChange={(event) => setPlanId(event.target.value)}
              disabled={tenant.stripeManaged || plans.isLoading}
              required
            >
              <option value='' disabled>
                Select a package
              </option>
              {plans.data
                ?.filter((plan) => plan.active || plan.id === tenant.planId)
                .map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} · {plan.deviceLimit} screens · {plan.userLimit}{' '}
                    users · {(plan.storageBytes / 1024 ** 3).toFixed(0)} GB
                  </option>
                ))}
            </select>
          </div>
          <div className='rounded-lg border bg-muted/30 p-4 text-sm leading-6'>
            <p>
              Current: <strong>{tenant.plan ?? 'No package'}</strong> ·{' '}
              {tenant.subscriptionStatus ?? 'No status'}
            </p>
            {selectedPlan && selectedPlan.id !== tenant.planId && (
              <p className='mt-2 text-muted-foreground'>
                New limits: {selectedPlan.deviceLimit} screens,{' '}
                {selectedPlan.userLimit} users,{' '}
                {(selectedPlan.storageBytes / 1024 ** 3).toFixed(0)} GB storage.
              </p>
            )}
          </div>
          {tenant.stripeManaged ? (
            <p className='rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm leading-6 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100'>
              This subscription is managed by Stripe. Change it from the tenant
              Billing workspace so invoices and renewal state remain
              synchronized.
            </p>
          ) : (
            <p className='rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm leading-6 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100'>
              This is a direct platform assignment. It changes entitlement
              immediately and does not create a Stripe charge. Assignment is
              blocked if current usage exceeds the new package limits.
            </p>
          )}
          <DialogFooter>
            <Button type='button' variant='outline' onClick={close}>
              Cancel
            </Button>
            <Button
              disabled={
                unchanged || tenant.stripeManaged || assignment.isPending
              }
            >
              {assignment.isPending ? 'Assigning…' : 'Assign package'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function PlatformTenantsPage() {
  const [selectedTenant, setSelectedTenant] = useState<PlatformTenant>()
  const [editingTenant, setEditingTenant] = useState<PlatformTenant>()
  const [packageTenant, setPackageTenant] = useState<PlatformTenant>()
  const [createOpen, setCreateOpen] = useState(false)
  const [createdTenant, setCreatedTenant] = useState<PlatformTenantCreation>()
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const queryClient = useQueryClient()
  const tenants = useQuery({
    queryKey: ['platform-tenants'],
    queryFn: () => api<PlatformTenant[]>('/platform/tenants'),
  })
  const access = useMutation({
    mutationFn: (input: {
      tenantId: string
      scope: 'READ_ONLY' | 'ADMIN'
      reason: string
      durationMinutes: 15 | 30 | 60
    }) =>
      api(`/platform/tenants/${input.tenantId}/access`, {
        method: 'POST',
        body: jsonBody({
          scope: input.scope,
          reason: input.reason,
          durationMinutes: input.durationMinutes,
        }),
      }),
    onSuccess: async () => {
      await refresh()
      setSelectedTenant(undefined)
      toast.success('Temporary tenant access started.')
      navigate('/app')
    },
    onError: (error) => toast.error(error.message),
  })
  const createTenant = useMutation({
    mutationFn: (input: {
      name: string
      ownerEmail: string
      timezone: string
    }) =>
      api<PlatformTenantCreation>('/platform/tenants', {
        method: 'POST',
        body: jsonBody(input),
      }),
    onSuccess: (created) => {
      queryClient.setQueryData<PlatformTenant[]>(
        ['platform-tenants'],
        (current) => [created.tenant, ...(current ?? [])]
      )
      setCreateOpen(false)
      setCreatedTenant(created)
      toast.success('Tenant created and owner invitation prepared.')
    },
    onError: (error) => toast.error(error.message),
  })
  const submitCreate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    createTenant.mutate({
      name: String(data.get('name') ?? ''),
      ownerEmail: String(data.get('ownerEmail') ?? ''),
      timezone: String(data.get('timezone') ?? ''),
    })
  }
  const submitAccess = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedTenant) return
    const data = new FormData(event.currentTarget)
    access.mutate({
      tenantId: selectedTenant.id,
      scope: String(data.get('scope')) as 'READ_ONLY' | 'ADMIN',
      reason: String(data.get('reason') ?? ''),
      durationMinutes: Number(data.get('durationMinutes')) as 15 | 30 | 60,
    })
  }
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Platform'
        title='Tenant operations'
        description='Cross-tenant visibility is restricted to MFA-authenticated platform administrators and every mutation is audited.'
        action={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className='size-4' />
            Add tenant
          </Button>
        }
      />
      <TableShell
        headers={[
          'Tenant',
          'Entitlement',
          'Screens',
          'Users',
          'Created',
          'Actions',
        ]}
      >
        {tenants.data?.map((tenant) => (
          <tr key={tenant.id}>
            <td className='px-4 py-3'>
              <p className='font-semibold'>{tenant.name}</p>
              <p className='mt-1 text-xs text-muted-foreground'>
                {tenant.slug} · {tenant.timezone}
              </p>
            </td>
            <td className='px-4 py-3'>
              <StatusPill value={tenant.subscriptionStatus ?? 'MISSING'} />
              <p className='mt-1 text-xs text-muted-foreground'>
                {tenant.plan ?? 'No plan'}
              </p>
            </td>
            <td className='px-4 py-3'>{tenant.devices}</td>
            <td className='px-4 py-3'>{tenant.users}</td>
            <td className='px-4 py-3 text-muted-foreground'>
              {new Date(tenant.createdAt).toLocaleDateString()}
            </td>
            <td className='px-4 py-3'>
              <div className='flex flex-wrap gap-2'>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => setEditingTenant(tenant)}
                  aria-label={`Manage ${tenant.name}`}
                >
                  <Pencil className='size-4' />
                  Manage
                </Button>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => setPackageTenant(tenant)}
                  aria-label={`Change ${tenant.name} package`}
                >
                  <Rocket className='size-4' />
                  Package
                </Button>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => setSelectedTenant(tenant)}
                  aria-label={`Open ${tenant.name} workspace`}
                >
                  <ExternalLink className='size-4' />
                  Open workspace
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </TableShell>
      {editingTenant && (
        <TenantManagementDialog
          tenant={editingTenant}
          close={() => setEditingTenant(undefined)}
        />
      )}
      {packageTenant && (
        <TenantPackageDialog
          key={packageTenant.id}
          tenant={packageTenant}
          close={() => setPackageTenant(undefined)}
        />
      )}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add tenant</DialogTitle>
            <DialogDescription>
              Create a 14-day trial workspace and prepare a seven-day,
              single-use owner invitation.
            </DialogDescription>
          </DialogHeader>
          <form className='space-y-4' onSubmit={submitCreate}>
            <div className='space-y-2'>
              <Label htmlFor='platform-tenant-name'>Organization name</Label>
              <Input
                id='platform-tenant-name'
                name='name'
                minLength={2}
                maxLength={120}
                required
                autoFocus
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='platform-tenant-owner'>Owner email</Label>
              <Input
                id='platform-tenant-owner'
                name='ownerEmail'
                type='email'
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='platform-tenant-timezone'>IANA timezone</Label>
              <Input
                id='platform-tenant-timezone'
                name='timezone'
                defaultValue={
                  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
                }
                required
              />
            </div>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button disabled={createTenant.isPending}>
                {createTenant.isPending ? 'Creating…' : 'Create tenant'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(createdTenant)}
        onOpenChange={(open) => !open && setCreatedTenant(undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tenant ready</DialogTitle>
            <DialogDescription>
              Share this single-use link with{' '}
              {createdTenant?.ownerInvitation.email}. It expires in seven days.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <Label htmlFor='platform-owner-invitation'>Owner invitation</Label>
            <Input
              id='platform-owner-invitation'
              value={createdTenant?.ownerInvitation.invitationUrl ?? ''}
              readOnly
            />
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => setCreatedTenant(undefined)}
            >
              Done
            </Button>
            <Button
              type='button'
              onClick={() => {
                const invitationUrl =
                  createdTenant?.ownerInvitation.invitationUrl
                if (!invitationUrl) return
                void navigator.clipboard
                  .writeText(invitationUrl)
                  .then(() => toast.success('Invitation link copied.'))
                  .catch(() => toast.error('Could not copy the link.'))
              }}
            >
              Copy invitation link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(selectedTenant)}
        onOpenChange={(open) => !open && setSelectedTenant(undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Open {selectedTenant?.name}</DialogTitle>
            <DialogDescription>
              Start a temporary, audited platform-support session. Read-only is
              the safest default; admin access can change tenant content and
              devices.
            </DialogDescription>
          </DialogHeader>
          <form className='space-y-4' onSubmit={submitAccess}>
            <div className='space-y-2'>
              <Label htmlFor='platform-access-scope'>Access level</Label>
              <select
                id='platform-access-scope'
                name='scope'
                defaultValue='READ_ONLY'
                className='h-10 w-full rounded-md border bg-background px-3 text-sm'
              >
                <option value='READ_ONLY'>Read-only support</option>
                <option value='ADMIN'>Temporary tenant admin</option>
              </select>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='platform-access-duration'>Duration</Label>
              <select
                id='platform-access-duration'
                name='durationMinutes'
                defaultValue='30'
                className='h-10 w-full rounded-md border bg-background px-3 text-sm'
              >
                <option value='15'>15 minutes</option>
                <option value='30'>30 minutes</option>
                <option value='60'>60 minutes</option>
              </select>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='platform-access-reason'>Access reason</Label>
              <Textarea
                id='platform-access-reason'
                name='reason'
                minLength={10}
                maxLength={500}
                placeholder='Example: Investigating player synchronization reported by the customer'
                required
              />
              <p className='text-xs text-muted-foreground'>
                The reason, access level, duration, account, IP, and tenant are
                written to the audit log.
              </p>
            </div>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => setSelectedTenant(undefined)}
              >
                Cancel
              </Button>
              <Button disabled={access.isPending}>
                {access.isPending ? 'Opening…' : 'Start tenant access'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function PlanEditorDialog({
  plan,
  close,
  save,
  pending,
}: {
  plan?: Plan
  close: () => void
  save: (input: PlanInput) => void
  pending: boolean
}) {
  const isTrial = plan?.code === 'trial'
  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className='max-h-[92vh] overflow-y-auto sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>
            {plan ? `Edit ${plan.name}` : 'Add package'}
          </DialogTitle>
          <DialogDescription>
            Configure the Stripe mapping and the limits enforced across tenant
            workspaces.
          </DialogDescription>
        </DialogHeader>
        <form
          className='space-y-5'
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            const data = new FormData(event.currentTarget)
            const storageGb = Number(data.get('storageGb'))
            const storageBytes = Math.round(storageGb * 1024 ** 3)
            if (!Number.isSafeInteger(storageBytes) || storageBytes < 1) {
              toast.error('Storage must be a positive number of GB.')
              return
            }
            const stripePriceId = String(data.get('stripePriceId') ?? '').trim()
            save({
              ...(!plan
                ? {
                    code: String(data.get('code') ?? '')
                      .trim()
                      .toLowerCase(),
                  }
                : {}),
              name: String(data.get('name') ?? '').trim(),
              stripePriceId: stripePriceId || null,
              deviceLimit: Number(data.get('deviceLimit')),
              userLimit: Number(data.get('userLimit')),
              storageBytes,
              active: data.get('active') === 'on',
              sortOrder: Number(data.get('sortOrder')),
            })
          }}
        >
          <div className='grid gap-4 sm:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='package-name'>Package name</Label>
              <Input
                id='package-name'
                name='name'
                defaultValue={plan?.name ?? ''}
                placeholder='Growth'
                minLength={2}
                maxLength={100}
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='package-code'>Package code</Label>
              <Input
                id='package-code'
                name='code'
                defaultValue={plan?.code ?? ''}
                placeholder='growth'
                pattern='[a-z0-9-]{2,50}'
                title='Use 2–50 lowercase letters, numbers, or hyphens.'
                readOnly={Boolean(plan)}
                required
              />
              {plan && (
                <p className='text-xs text-muted-foreground'>
                  Package codes are permanent identifiers and cannot be edited.
                </p>
              )}
            </div>
            <div className='space-y-2 sm:col-span-2'>
              <Label htmlFor='package-stripe-price'>Stripe Price ID</Label>
              <Input
                id='package-stripe-price'
                name='stripePriceId'
                defaultValue={plan?.stripePriceId ?? ''}
                placeholder='price_...'
                maxLength={255}
              />
              <p className='text-xs text-muted-foreground'>
                Optional for the trial package or an inactive draft. Active paid
                packages require a unique Price ID.
              </p>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='package-screens'>Screen limit</Label>
              <Input
                id='package-screens'
                name='deviceLimit'
                type='number'
                min={1}
                max={10000}
                defaultValue={plan?.deviceLimit ?? 3}
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='package-users'>User limit</Label>
              <Input
                id='package-users'
                name='userLimit'
                type='number'
                min={1}
                max={10000}
                defaultValue={plan?.userLimit ?? 5}
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='package-storage'>Storage (GB)</Label>
              <Input
                id='package-storage'
                name='storageGb'
                type='number'
                min='0.01'
                step='0.01'
                defaultValue={plan ? plan.storageBytes / 1024 ** 3 : 5}
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='package-sort-order'>Sort order</Label>
              <Input
                id='package-sort-order'
                name='sortOrder'
                type='number'
                min={-10000}
                max={10000}
                defaultValue={plan?.sortOrder ?? 0}
                required
              />
            </div>
          </div>
          <label className='flex items-start gap-3 rounded-lg border p-4'>
            <input
              aria-label='Package active'
              name='active'
              type='checkbox'
              defaultChecked={plan?.active ?? false}
              className='mt-1 size-4 accent-[var(--tenant-brand)]'
            />
            <span>
              <span className='block text-sm font-semibold'>
                Active package
              </span>
              <span className='mt-1 block text-xs leading-5 text-muted-foreground'>
                Active packages can be selected by billing and signup flows.
              </span>
            </span>
          </label>
          {plan && (
            <div className='rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm leading-6 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100'>
              Limit changes apply immediately to tenants subscribed to this
              package. A new Stripe Price ID applies to future checkout or plan
              changes; existing Stripe subscriptions remain on their current
              price.
            </div>
          )}
          {isTrial && (
            <p className='text-xs text-muted-foreground'>
              The internal trial package does not require a Stripe Price ID.
            </p>
          )}
          <DialogFooter>
            <Button type='button' variant='outline' onClick={close}>
              Cancel
            </Button>
            <Button disabled={pending}>
              {pending
                ? 'Saving…'
                : plan
                  ? 'Save package changes'
                  : 'Create package'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function PlatformPlansPage() {
  const queryClient = useQueryClient()
  const [dialog, setDialog] = useState<{ plan?: Plan }>()
  const plans = useQuery({
    queryKey: ['platform-plans'],
    queryFn: () => api<Plan[]>('/platform/plans'),
  })
  const savePlan = useMutation({
    mutationFn: (input: { planId?: string; payload: PlanInput }) =>
      api<Plan>(
        input.planId ? `/platform/plans/${input.planId}` : '/platform/plans',
        {
          method: input.planId ? 'PATCH' : 'POST',
          body: jsonBody(input.payload),
        }
      ),
    onSuccess: async (_saved, input) => {
      setDialog(undefined)
      await queryClient.invalidateQueries({ queryKey: ['platform-plans'] })
      toast.success(input.planId ? 'Package updated.' : 'Package created.')
    },
    onError: (error) => toast.error(error.message),
  })
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Platform'
        title='Plans & quotas'
        description='Price IDs and all entitlement limits are data-driven. Monetary values remain in Stripe.'
        action={
          <Button onClick={() => setDialog({})}>
            <Plus className='size-4' />
            Add package
          </Button>
        }
      />
      <TableShell
        headers={[
          'Plan',
          'Stripe Price',
          'Screens',
          'Users',
          'Storage',
          'State',
          'Actions',
        ]}
      >
        {plans.data?.map((plan) => (
          <tr key={plan.id}>
            <td className='px-4 py-3'>
              <p className='font-semibold'>{plan.name}</p>
              <p className='mt-1 font-mono text-xs text-muted-foreground'>
                {plan.code}
              </p>
            </td>
            <td className='px-4 py-3 font-mono text-xs text-muted-foreground'>
              {plan.stripePriceId ?? 'Internal trial'}
            </td>
            <td className='px-4 py-3'>{plan.deviceLimit}</td>
            <td className='px-4 py-3'>{plan.userLimit}</td>
            <td className='px-4 py-3'>
              {(plan.storageBytes / 1024 ** 3).toFixed(0)} GB
            </td>
            <td className='px-4 py-3'>
              <StatusPill value={plan.active ? 'ACTIVE' : 'INACTIVE'} />
            </td>
            <td className='px-4 py-3'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                aria-label={`Edit ${plan.name}`}
                onClick={() => setDialog({ plan })}
              >
                <Pencil className='size-4' />
                Edit
              </Button>
            </td>
          </tr>
        ))}
      </TableShell>
      {dialog && (
        <PlanEditorDialog
          key={dialog.plan?.id ?? 'new-package'}
          plan={dialog.plan}
          close={() => setDialog(undefined)}
          pending={savePlan.isPending}
          save={(payload) =>
            savePlan.mutate({ planId: dialog.plan?.id, payload })
          }
        />
      )}
    </div>
  )
}

export function PlatformReleasesPage() {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File>()
  const [uploadStage, setUploadStage] = useState<string>()
  const releases = useQuery({
    queryKey: ['platform-releases'],
    queryFn: () => api<Release[]>('/platform/apk-releases'),
    refetchInterval: (query) =>
      query.state.data?.some((release) =>
        ['VALIDATING', 'PROCESSING'].includes(release.status)
      )
        ? 5_000
        : false,
  })

  const upload = useMutation({
    mutationFn: async (input: {
      file: File
      versionCode: number
      versionName: string
      minimumVersionCode: number
      releaseNotes?: string
    }) => {
      setUploadStage('Creating a private upload…')
      const created = await api<ReleaseUpload>('/platform/apk-releases', {
        method: 'POST',
        body: jsonBody({
          versionCode: input.versionCode,
          versionName: input.versionName,
          minimumVersionCode: input.minimumVersionCode,
          sizeBytes: input.file.size,
          releaseNotes: input.releaseNotes || undefined,
        }),
      })
      setUploadStage('Uploading APK directly to private storage…')
      const uploaded = await fetch(created.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/vnd.android.package-archive',
        },
        body: input.file,
      })
      if (!uploaded.ok) {
        throw new Error(`Private upload failed with HTTP ${uploaded.status}.`)
      }
      setUploadStage('Queueing checksum and signature validation…')
      return api<Release>(`/platform/apk-releases/${created.id}/complete`, {
        method: 'POST',
      })
    },
    onSuccess: async () => {
      setFile(undefined)
      setUploadStage(undefined)
      await queryClient.invalidateQueries({
        queryKey: ['platform-releases'],
      })
      toast.success('APK uploaded. Validation is running.')
    },
    onError: (error) => {
      setUploadStage(undefined)
      toast.error(error.message)
    },
  })

  const rollout = useMutation({
    mutationFn: (input: {
      releaseId: string
      rolloutPercent: 0 | 5 | 25 | 100
      paused: boolean
    }) =>
      api<Release>(`/platform/apk-releases/${input.releaseId}/rollout`, {
        method: 'PATCH',
        body: jsonBody({
          rolloutPercent: input.rolloutPercent,
          paused: input.paused,
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['platform-releases'],
      })
    },
    onError: (error) => toast.error(error.message),
  })

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!file || !file.name.toLowerCase().endsWith('.apk')) {
      toast.error('Choose an Android APK file.')
      return
    }
    const data = new FormData(event.currentTarget)
    upload.mutate({
      file,
      versionCode: Number(data.get('versionCode')),
      versionName: String(data.get('versionName') ?? ''),
      minimumVersionCode: Number(data.get('minimumVersionCode')),
      releaseNotes: String(data.get('releaseNotes') ?? ''),
    })
  }

  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Private OTA'
        title='Android releases'
        description='APK package, checksum, signing certificate, malware scan, and version metadata are verified before progressive 5% → 25% → 100% rollout.'
      />
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>Upload signed player APK</CardTitle>
        </CardHeader>
        <CardContent>
          <form className='grid gap-4 lg:grid-cols-5' onSubmit={submit}>
            <div className='space-y-2 lg:col-span-2'>
              <Label htmlFor='apk-file'>APK file</Label>
              <Input
                id='apk-file'
                type='file'
                accept='.apk,application/vnd.android.package-archive'
                required
                onChange={(event) => setFile(event.target.files?.[0])}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='version-name'>Version name</Label>
              <Input
                id='version-name'
                name='versionName'
                placeholder='1.1.0'
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='version-code'>Version code</Label>
              <Input
                id='version-code'
                name='versionCode'
                type='number'
                min={1}
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='minimum-version'>Minimum installed code</Label>
              <Input
                id='minimum-version'
                name='minimumVersionCode'
                type='number'
                min={1}
                defaultValue={1}
                required
              />
            </div>
            <div className='space-y-2 lg:col-span-4'>
              <Label htmlFor='release-notes'>Release notes</Label>
              <Input
                id='release-notes'
                name='releaseNotes'
                placeholder='Reliability and playback improvements'
              />
            </div>
            <div className='flex items-end'>
              <Button className='w-full' disabled={upload.isPending || !file}>
                <Upload className='size-4' />
                {upload.isPending ? 'Uploading…' : 'Upload & validate'}
              </Button>
            </div>
          </form>
          {uploadStage && (
            <p className='mt-4 text-sm text-muted-foreground' role='status'>
              {uploadStage}
            </p>
          )}
        </CardContent>
      </Card>
      <TableShell
        headers={[
          'Version',
          'State',
          'Rollout',
          'Identity',
          'Checksum',
          'Created',
        ]}
      >
        {releases.data?.map((release) => {
          const rolloutReady = ['READY', 'ACTIVE'].includes(release.status)
          return (
            <tr key={release.id}>
              <td className='px-4 py-3'>
                <p className='font-semibold'>{release.versionName}</p>
                <p className='mt-1 text-xs text-muted-foreground'>
                  code {release.versionCode} · minimum{' '}
                  {release.minimumVersionCode}
                </p>
              </td>
              <td className='max-w-52 px-4 py-3'>
                <StatusPill
                  value={
                    release.paused && release.rolloutPercent > 0
                      ? 'PAUSED'
                      : release.status
                  }
                />
                {release.validationError && (
                  <p className='mt-2 text-xs text-red-600'>
                    {release.validationError}
                  </p>
                )}
              </td>
              <td className='px-4 py-3'>
                <p className='mb-2 font-bold'>{release.rolloutPercent}%</p>
                {rolloutReady && (
                  <div className='flex flex-wrap gap-1'>
                    {([5, 25, 100] as const).map((percent) => (
                      <Button
                        key={percent}
                        size='sm'
                        variant={
                          release.rolloutPercent === percent
                            ? 'default'
                            : 'outline'
                        }
                        disabled={
                          rollout.isPending ||
                          release.rolloutPercent === percent
                        }
                        onClick={() =>
                          rollout.mutate({
                            releaseId: release.id,
                            rolloutPercent: percent,
                            paused: false,
                          })
                        }
                      >
                        {percent === 100 && <Rocket className='size-3' />}
                        {percent}%
                      </Button>
                    ))}
                    {release.rolloutPercent > 0 && (
                      <Button
                        size='sm'
                        variant='outline'
                        disabled={rollout.isPending}
                        onClick={() =>
                          rollout.mutate({
                            releaseId: release.id,
                            rolloutPercent: release.rolloutPercent as
                              5 | 25 | 100,
                            paused: !release.paused,
                          })
                        }
                      >
                        {release.paused ? (
                          <Play className='size-3' />
                        ) : (
                          <Pause className='size-3' />
                        )}
                        {release.paused ? 'Resume' : 'Pause'}
                      </Button>
                    )}
                  </div>
                )}
              </td>
              <td className='px-4 py-3 font-mono text-xs'>
                <p>{release.packageName}</p>
                <p className='mt-1 text-muted-foreground'>
                  cert {release.certificateSha256.slice(0, 12)}…
                </p>
              </td>
              <td className='px-4 py-3 font-mono text-xs text-muted-foreground'>
                {release.checksumSha256.slice(0, 12)}…
              </td>
              <td className='px-4 py-3 text-muted-foreground'>
                {new Date(release.createdAt).toLocaleDateString()}
              </td>
            </tr>
          )
        })}
      </TableShell>
    </div>
  )
}

export function PlatformHealthPage() {
  const health = useQuery({
    queryKey: ['platform-health'],
    queryFn: () =>
      api<Record<string, number | string>>('/platform/system-health'),
    refetchInterval: 30_000,
  })
  const entries = Object.entries(health.data ?? {}).filter(
    ([key]) => key !== 'timestamp'
  )
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Platform'
        title='System health'
        description='A compact operational view of fleet connectivity, background processing, Stripe events, and notification delivery.'
      />
      <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
        {entries.map(([label, value]) => (
          <Card key={label}>
            <CardHeader>
              <CardTitle className='text-xs tracking-wider text-muted-foreground uppercase'>
                {label.replace(/([A-Z])/g, ' $1')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className='text-3xl font-black'>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
